import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Box, Card, CardContent, Skeleton, Grid, Typography, Tabs, Tab } from '@mui/material'
import Products from '../../../components/GenericProductConfig/components/Products/AllProducts'
import ChangeRequestsScreen from '../components/ChangeRequests/ChangeRequestsScreen'
import TemplatesScreen from '../components/Templates/TemplatesScreen'
import { getPartnerGuid, getPartner, hasAuthourity } from '../../../lib/access.es6'
import * as actions from '../actions.es6.js'

// Sub-sections of the Generic Products module: the product list, the archetype
// template catalog, and maker-checker change requests. Rating-table management
// now lives inside each product's editor (and the review-draft screen), not at
// this list level.
const MODULE_TABS = ['Product List', 'Templates', 'Change Requests']

const ViewProducts = props => {
  const partner = getPartnerGuid()
  const partnerObj = getPartner()
  const partnerName = (partnerObj && partnerObj.name) || partner
  const [moduleTab, setModuleTab] = useState(0)

  const { dispatch } = props

  useEffect(() => {
    // Fetch once per partner. NOTE: do NOT put `props` in the deps — with
    // connect() it's a new object every store update, so the effect would
    // re-fire on each render and re-dispatch the fetch (which resets
    // loader:true + clears products), leaving the list stuck loading forever.
    if (hasAuthourity('VIEW_ALL_PRODUCTS')) {
      dispatch(
        actions.getAllProductsRequest({
          partnerGuid: partner,
          activeOnly: false
        })
      )
    }
  }, [partner, dispatch])

  const handleCreateProductInfo = data => {
    props.dispatch(actions.createProductRequest({ ...data, partner, partnerGuid: partner }))
  }

  // Skeleton component for loading state
  const ProductCardSkeleton = () => (
    <Card
      sx={{
        border: '1px solid #fef3e2',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={120} height={28} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
          </Box>
        </Box>

        <Skeleton
          variant="rectangular"
          width="100%"
          height={1}
          sx={{ my: 2 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={40} height={16} />
              <Skeleton
                variant="rectangular"
                width={60}
                height={24}
                sx={{ borderRadius: 3 }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={40} height={16} />
              <Skeleton
                variant="rectangular"
                width={80}
                height={24}
                sx={{ borderRadius: 3 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={120} height={16} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )

  const LoadingSkeleton = () => (
    <Box>
      {/* Header skeleton */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4
        }}
      >
        <Box>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="text" width={300} height={20} />
        </Box>
        <Skeleton
          variant="rectangular"
          width={140}
          height={40}
          sx={{ borderRadius: 1 }}
        />
      </Box>

      {/* Products grid skeleton */}
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map(item => (
          <Grid item xs={12} md={6} key={item}>
            <ProductCardSkeleton />
          </Grid>
        ))}
      </Grid>
    </Box>
  )

  const productList = props.productsData.loader ? (
    <LoadingSkeleton />
  ) : props.productsData.error ? (
    <div>Error: {props.productsData.error}</div>
  ) : (
    <Products
      products={props.productsData.products}
      onProductInfoSubmit={handleCreateProductInfo}
      isLoading={props.productsData.productLoader}
      genericProductsData={props.genericProductsData}
      dispatch={props.dispatch}
      productSuccess={props.productsData.productSuccess}
      productError={props.productsData.productError}
      resetProductSuccess={function() { props.dispatch(actions.resetCreateProductSuccess()); }}
      resetProductError={function() { props.dispatch(actions.resetCreateProductError()); }}
    />
  )

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
      {/* Module header — title + active partner */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#173E4B' }}>
          Generic Products
        </Typography>
        <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{partnerName}</Typography>
      </Box>

      {/* Module tab strip */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={moduleTab}
          onChange={(e, v) => setModuleTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': { backgroundColor: '#173E4B' },
            '& .MuiTab-root': {
              color: '#000000',
              textTransform: 'none',
              minHeight: 48,
              fontWeight: 600,
              '&.Mui-selected': { color: '#173E4B' },
              '&:hover': { color: '#173E4B' }
            }
          }}
        >
          {MODULE_TABS.map(label => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Card>

      {moduleTab === 0 && productList}
      {moduleTab === 1 && <TemplatesScreen />}
      {moduleTab === 2 && <ChangeRequestsScreen />}
    </Box>
  )
}

export default connect(state => ({
  productsData: state.newProducts,
  globalData: state.global,
  genericProductsData: state.genericProducts
}))(ViewProducts)
